import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Col, getItemWithExpiration } from '../../lib/utils';
import UpdateOneSchedule from '../../components/UpdateSchedule/UpdateOneSchedule/UpdateOneSchedule';
import UpdateEtcSchedule from '../../components/UpdateSchedule/UpdateEtcSchedule/UpdateEtcSchedule';
import { colors } from '../../lib/colors';
import { useQuery } from '@tanstack/react-query';
import { getCoachListAPI } from '../../api/schedule';
import _ from 'lodash';
import dayjs from 'dayjs';
import BusinessInfoFooter from '../../components/BusinessInfoFooter';
import { usePublicMemberDetail } from '../../features/publicMember/hooks';
import { resolveHistoryMemberId } from '../../lib/scheduleHistory';
import {
    hasExplicitScheduleAppAccountStatus,
    isWithdrawnScheduleReadOnly,
} from '../../lib/scheduleMemberLink';

type PageType = 'oneSchedule' | 'repeatSchedule' | 'etcSchedule' | 'offSchedule';

const UpdateSchedulePage = ({ createAlert, setIsLoading }: any) => {
    const [authorization, setAuthorization] = useState(getItemWithExpiration('authorization'));
    const [authObject, setAuthObject] = useState(getItemWithExpiration('authObject'));
    const location = useLocation();
    const navigate = useNavigate();

    const pageType: PageType = location.state?.type;
    const scheduleObject: any = location.state?.scheduleObject;
    const memberId = resolveHistoryMemberId(scheduleObject) || '';
    const shouldLoadMemberAccountStatus =
        pageType === 'oneSchedule' &&
        !!memberId &&
        !hasExplicitScheduleAppAccountStatus(scheduleObject);
    const memberAccountQuery = usePublicMemberDetail({
        centerId: authObject?.centerId || '',
        memberId,
        enabled: shouldLoadMemberAccountStatus,
    });
    const isMemberAccountStatusPending =
        shouldLoadMemberAccountStatus && memberAccountQuery.isLoading;
    const isMemberAccountStatusUnavailable =
        shouldLoadMemberAccountStatus && memberAccountQuery.isError;
    const isWithdrawalReadOnly =
        pageType === 'oneSchedule' &&
        (isWithdrawnScheduleReadOnly(scheduleObject) ||
            isWithdrawnScheduleReadOnly(memberAccountQuery.data?.centerMember));
    const isMutationBlocked =
        isWithdrawalReadOnly || isMemberAccountStatusPending || isMemberAccountStatusUnavailable;

    // 캘린더 페이지의 일간 주간 타입 정보
    const calendarType = location.state?.calendarType;

    // 일정 페이지로 갔다가 되돌아올 때 받아올 날짜 및 스크롤 정보
    const calendarSelectedDay = location.state?.calendarSelectedDay;
    const calendarScrollY = location.state?.calendarScrollY;

    const [coachOption, setCoachOption] = useState<any>([]);

    //  강사 리스트 조회 API : (GET)
    const getCoachList = useQuery(
        /* 생성 화면과 **같은 키**를 공유한다 — centerId 도 함께 맞춘다 */
        ['getCoachListAPI', authObject?.centerId],
        async () => await getCoachListAPI(authObject?.centerId, authorization),
        {
            onSuccess: (res) => {
                if (res.status === 200) {
                    const response = res?.data?.coachNameList;
                    const coachList = [];

                    if (response && response.length > 0) {
                        for (let i = 0; i < response.length; i++) {
                            coachList.push({ value: response[i].coachId, label: response[i].name });
                        }
                    }

                    setCoachOption(coachList);
                }
            },
            onError: (err) => {
                console.log(err);
            },
            enabled: !!authorization && !!!authObject?.testAccount && !isMutationBlocked,
            // refetchOnWindowFocus: 'always',
            staleTime: 0,
        }
    );

    return (
        <Col style={{ width: '100%', alignItems: 'center', marginTop: 64, marginBottom: 80 }}>
            <Col
                style={{
                    width: 1192,
                    minHeight: 400,
                    borderRadius: 16,
                    backgroundColor: colors.WHITE_50,
                    border: '1px solid #C3D4F4',
                    marginTop: 64,
                }}
            >
                {pageType === 'oneSchedule' && isMutationBlocked ? (
                    <Col
                        style={{
                            minHeight: 400,
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 20,
                            color: colors.LAVEL_4,
                        }}
                    >
                        <strong style={{ fontSize: 18 }}>
                            {isMemberAccountStatusPending
                                ? '회원 상태를 확인하고 있습니다.'
                                : isMemberAccountStatusUnavailable
                                ? '회원 상태를 확인할 수 없어 수정을 진행할 수 없습니다.'
                                : '탈퇴 회원의 일정은 조회만 가능합니다.'}
                        </strong>
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            style={{
                                height: 40,
                                padding: '0 20px',
                                borderRadius: 8,
                                border: '1px solid #C3D4F4',
                                backgroundColor: colors.WHITE_50,
                                color: colors.LAVEL_4,
                                cursor: 'pointer',
                                fontWeight: 700,
                            }}
                        >
                            이전 화면으로
                        </button>
                    </Col>
                ) : pageType === 'oneSchedule' ? (
                    <UpdateOneSchedule
                        coachOption={coachOption}
                        authorization={authorization}
                        authObject={authObject}
                        createAlert={createAlert}
                        scheduleObject={scheduleObject}
                        calendarType={calendarType}
                        calendarSelectedDay={dayjs(calendarSelectedDay).format('YYYY-MM-DD')}
                        calendarScrollY={calendarScrollY}
                    />
                ) : null}
                {pageType === 'etcSchedule' && (
                    <UpdateEtcSchedule
                        coachOption={coachOption}
                        authorization={authorization}
                        authObject={authObject}
                        createAlert={createAlert}
                        scheduleObject={scheduleObject}
                        calendarType={calendarType}
                        calendarSelectedDay={dayjs(calendarSelectedDay).format('YYYY-MM-DD')}
                        calendarScrollY={calendarScrollY}
                    />
                )}
            </Col>

            {/* 사업자 정보 (카드사 심사용) */}
            <div style={{ width: '100%', marginTop: 40 }}>
                <BusinessInfoFooter />
            </div>
        </Col>
    );
};

export default UpdateSchedulePage;
